/**
 * Transaction collection helpers.
 * Implements an escrow flow:
 *   initiated → payment_sent (escrow) → delivery_confirmed → completed
 *   Any state can transition to 'cancelled' / 'disputed'.
 */
import { transactionsRef } from './refs';
import { serverTimestamp } from './fieldValues';

export const getTransactionRef = (id) => transactionsRef().doc(id);
export const getTransactionsForBuyer = (buyerId, limit = 50) =>
  transactionsRef()
    .where('buyerId', '==', buyerId)
    .orderBy('createdAt', 'desc')
    .limit(limit);
export const getTransactionsForSeller = (sellerId, limit = 50) =>
  transactionsRef()
    .where('sellerId', '==', sellerId)
    .orderBy('createdAt', 'desc')
    .limit(limit);

export async function createTransaction(data) {
  const now = serverTimestamp();

  // Calculate commission and seller's net from token config
  const amount = data.amount ?? 0;
  const commissionPercent = Number(data.commissionPercent) || 0;
  const commissionAmount = data.commissionAmount ?? Math.round(amount * commissionPercent) / 100;
  const sellerReceives = data.sellerReceives ?? (amount - commissionAmount);

  const ref = await transactionsRef().add({
    productId: data.productId || '',
    productName: data.productName || '',
    productImage: data.productImage || '',
    buyerId: data.buyerId || '',
    buyerName: data.buyerName || '',
    sellerId: data.sellerId || '',
    sellerName: data.sellerName || '',
    offerId: data.offerId || '',
    amount,
    originalPrice: data.originalPrice ?? 0,
    commissionAmount,
    commissionPercent,
    sellerReceives,
    tokenKind: data.tokenKind || 'standard',
    tokenName: data.tokenName || 'CXBT',
    tokenDecimals: data.tokenDecimals ?? 6,
    paymentMethod: data.paymentMethod || 'token',
    paymentStatus: 'pending',
    paymentReference: data.paymentReference || '',
    deliveryMethod: data.deliveryMethod || '',
    deliveryStatus: 'pending',
    deliveryAddress: data.deliveryAddress || {},
    trackingId: data.trackingId || '',
    meetingLocation: data.meetingLocation || {},
    meetingTime: data.meetingTime ?? null,
    // Escrow state: 'initiated' → 'payment_sent' → 'delivery_confirmed' → 'completed'
    status: 'initiated',
    escrowStatus: 'none',
    completedAt: null,
    cancelledAt: null,
    cancelReason: '',
    buyerConfirmed: false,
    sellerConfirmed: false,
    reviewId: '',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

/**
 * Mark payment as sent (funds locked in escrow).
 */
export async function markPaymentSent(transactionId, paymentReference) {
  return transactionsRef().doc(transactionId).update({
    paymentStatus: 'escrow_held',
    paymentReference: paymentReference || '',
    escrowStatus: 'held',
    status: 'payment_sent',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Buyer confirms delivery → release escrow to seller (minus commission).
 */
export async function confirmDelivery(transactionId) {
  const ref = transactionsRef().doc(transactionId);
  const doc = await ref.get();
  if (!doc.exists) throw new Error('Transaction not found');
  const data = doc.data();
  // Only the buyer can confirm delivery
  return ref.update({
    deliveryStatus: 'confirmed',
    buyerConfirmed: true,
    escrowStatus: 'released',
    paymentStatus: 'released',
    status: 'completed',
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancel a transaction.
 */
export async function cancelTransaction(transactionId, reason = '') {
  return transactionsRef().doc(transactionId).update({
    status: 'cancelled',
    escrowStatus: 'cancelled',
    paymentStatus: 'cancelled',
    cancelReason: reason,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
